import { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Source, Layer, Marker, Popup } from 'react-map-gl/mapbox';
import type { MapRef } from 'react-map-gl/mapbox';
import MapEngine from '../components/mapping/MapEngine';
import {
  MapIcon,
  BuildingOffice2Icon,
  AdjustmentsHorizontalIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { toPng } from 'html-to-image';
import { craApi, fairLendingApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import { usePrefetchMapLibrary } from '../hooks/usePrefetchMapLibrary';
// Shared canonical palette (CLARID-UX-2026-06, PR 8/9). This map IS the
// authoritative source (Wallace 2026-04-28 spec) — now read from the shared
// module so the CRA map can converge on it (PR 9 follow-up).
import {
  INCOME_COLORS,
  INCOME_LABELS,
  MMCT_COLORS,
  ACTION_COLORS,
  incomePaintExpression,
} from '../lib/mapping/mapColors';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

type ColorLayer = 'INCOME' | 'MMCT';
type LoanDisplay = 'OFF' | 'ORIGINATIONS' | 'ALL_APPS';

interface MapData {
  tracts: { type: 'FeatureCollection'; features: any[] };
  branches: Array<{
    id: string; branchName: string; locationType: string; isMainOffice: boolean;
    address: string; city: string; state: string; zip: string;
    latitude: number; longitude: number;
  }>;
  loans: Array<{
    id: string; tractCode: string | null; latitude: number; longitude: number;
    actionTaken: string; actionTakenLabel: string;
    loanAmount: number | null; loanPurpose: string | null;
  }>;
  bounds: [[number, number], [number, number]] | null;
  summary: {
    totalTracts: number; lmiTracts: number; mmctTracts: number;
    mmctMethodology: string | null; branchCount: number; loanCount: number;
    validationId: string | null; validationCalendarYear: string | null;
  };
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 60);
}

export default function FairLendingRedliningMapPage() {
  const { teamId } = useTeamId();
  usePrefetchMapLibrary();
  const mapRef = useRef<MapRef | null>(null);
  const exportRootRef = useRef<HTMLDivElement | null>(null);
  const [exporting, setExporting] = useState(false);

  const [selectedAaId, setSelectedAaId] = useState('');
  const [loanSource, setLoanSource] = useState<'hmda' | 'alert'>('hmda');
  const [colorLayer, setColorLayer] = useState<ColorLayer>('INCOME');
  const [showBranches, setShowBranches] = useState(true);
  const [showDistribution, setShowDistribution] = useState(false);
  const [loanDisplay, setLoanDisplay] = useState<LoanDisplay>('OFF');
  const [showPurchased, setShowPurchased] = useState(false);
  const [hoveredTract, setHoveredTract] = useState<{ x: number; y: number; props: any } | null>(null);
  const [popupBranch, setPopupBranch] = useState<MapData['branches'][number] | null>(null);
  const [popupLoan, setPopupLoan] = useState<MapData['loans'][number] | null>(null);

  // Load AAs
  const { data: aaData } = useQuery({
    queryKey: ['cra-assessment-areas', teamId],
    queryFn: async () => (await craApi.listAssessmentAreas(teamId!)).data,
    enabled: !!teamId,
  });
  const aas = useMemo(() => (aaData?.assessmentAreas || []).filter((a: any) => a.isActive !== false), [aaData]);

  // Default to first AA
  useEffect(() => {
    if (!selectedAaId && aas.length > 0) setSelectedAaId(aas[0].id);
  }, [aas, selectedAaId]);

  // Load map data
  const { data: mapData, isLoading: mapLoading, error } = useQuery<MapData>({
    queryKey: ['fl-redlining-map', teamId, selectedAaId, loanSource],
    queryFn: async () => (await fairLendingApi.getRedliningMap(teamId!, selectedAaId, loanSource)).data,
    enabled: !!teamId && !!selectedAaId,
    staleTime: 5 * 60 * 1000, // TIGERweb is slow; cache 5 min
  });

  // Distribution overlay (#3 phase 1b) — which tracts ads reached + MMCT/LMI coverage.
  const { data: distOverlay } = useQuery({
    queryKey: ['fl-distribution-overlay', teamId, selectedAaId],
    queryFn: async () => (await fairLendingApi.getDistributionOverlay(teamId!, selectedAaId)).data,
    enabled: !!teamId && !!selectedAaId && showDistribution,
    staleTime: 5 * 60 * 1000,
  });
  const reachedTractCodes: string[] = distOverlay?.reachedTractCodes ?? [];

  // Auto-center on AA bounds when data lands
  useEffect(() => {
    if (!mapData?.bounds || !mapRef.current) return;
    const [[minLat, minLng], [maxLat, maxLng]] = mapData.bounds;
    mapRef.current.fitBounds(
      [[minLng, minLat], [maxLng, maxLat]],
      { padding: 60, duration: 1200 },
    );
  }, [mapData]);

  // Filter loan dots based on display + purchased toggle
  const visibleLoans = useMemo(() => {
    if (!mapData) return [];
    if (loanDisplay === 'OFF') return [];
    return mapData.loans.filter(l => {
      if (loanDisplay === 'ORIGINATIONS') return l.actionTaken === '1';
      // ALL_APPS: include all action codes; purchased optional
      if (l.actionTaken === '6') return showPurchased;
      return true;
    });
  }, [mapData, loanDisplay, showPurchased]);

  // PNG export. Mapbox requires preserveDrawingBuffer:true (set on <Map>)
  // for getCanvas().toDataURL to work; html-to-image reads through the
  // canvas the same way once that's enabled.
  async function handleExportPng() {
    if (!exportRootRef.current || exporting) return;
    setExporting(true);
    try {
      // Clear any hover/popup state so they don't appear in the screenshot.
      setHoveredTract(null);
      setPopupBranch(null);
      setPopupLoan(null);
      // Let the next render flush before capture
      await new Promise(r => setTimeout(r, 50));

      const dataUrl = await toPng(exportRootRef.current, {
        backgroundColor: '#ffffff',
        cacheBust: true,
        pixelRatio: 2,
        filter: (node) =>
          !(node instanceof HTMLElement && node.dataset.exportIgnore === 'true'),
      });

      const aaName = aas.find((a: any) => a.id === selectedAaId)?.name;
      const stamp = new Date().toISOString().slice(0, 10);
      const slug = aaName ? slugify(aaName) : selectedAaId.slice(0, 8);
      const layerLabel = colorLayer === 'MMCT' ? 'mmct' : 'income';
      const filename = `redlining-${slug || 'map'}-${layerLabel}-${stamp}.png`;

      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } finally {
      setExporting(false);
    }
  }

  // GeoJSON enrichment for the color layer paint expression
  const tractFillExpression = useMemo<any>(() => {
    if (colorLayer === 'INCOME') {
      return incomePaintExpression();
    }
    return [
      'case',
      ['==', ['get', 'isMajorityMinority'], true], MMCT_COLORS.MMCT,
      MMCT_COLORS.NON_MMCT,
    ];
  }, [colorLayer]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center">
        <MapIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-navy-900">Mapbox not configured</h1>
        <p className="text-sm text-gray-500 mt-2">
          Set <code className="bg-gray-100 px-1.5 rounded">VITE_MAPBOX_TOKEN</code> in the frontend environment and redeploy.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-[100rem] mx-auto">
      <div className="mb-4 flex items-start gap-3">
        <div className="rounded-lg bg-ocean-50 p-2 text-ocean-600">
          <MapIcon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-navy-900">Fair Lending — Redlining Map</h1>
          <p className="mt-1 text-sm text-gray-600">
            Census tracts colored by CRA income tier or regulator-specific MMCT classification.
            Branch and LPO locations and HMDA loan dot density overlay on demand.
          </p>
        </div>
      </div>

      {/* AA selector */}
      <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[280px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Area</label>
          <select
            value={selectedAaId}
            onChange={(e) => setSelectedAaId(e.target.value)}
            disabled={aas.length === 0}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500 disabled:bg-gray-100"
          >
            {aas.length === 0 && <option value="">No assessment areas defined</option>}
            {aas.map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        {/* Loan source toggle — HMDA LAR vs the ALERT/ILDR non-HMDA book */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Loan Source</label>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            {(['hmda', 'alert'] as const).map((src) => (
              <button
                key={src}
                type="button"
                onClick={() => setLoanSource(src)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  loanSource === src
                    ? 'bg-ocean-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {src === 'hmda' ? 'HMDA LAR' : 'ALERT / ILDR'}
              </button>
            ))}
          </div>
        </div>
        {mapData?.summary && (
          <div className="text-xs text-gray-500 flex items-center gap-4">
            <span><span className="font-semibold text-navy-900">{mapData.summary.totalTracts}</span> tracts</span>
            <span><span className="font-semibold text-red-600">{mapData.summary.lmiTracts}</span> LMI</span>
            <span><span className="font-semibold text-purple-600">{mapData.summary.mmctTracts}</span> MMCT ({mapData.summary.mmctMethodology || 'auto'})</span>
            <span><span className="font-semibold text-blue-600">{mapData.summary.branchCount}</span> branches</span>
            <span><span className="font-semibold text-green-600">{mapData.summary.loanCount}</span> loans</span>
          </div>
        )}
      </div>

      {/* Empty-state: ALERT source selected but no loan trial balance ingested yet. */}
      {loanSource === 'alert' && !mapLoading && mapData && (mapData.summary?.loanCount ?? 0) === 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ocean-200 bg-ocean-50 px-4 py-3">
          <p className="text-sm text-navy-800">
            No ALERT / ILDR loans ingested for this team yet — there's nothing to plot on the
            non-HMDA book. Upload a loan trial balance to map your consumer, small-business, and
            small-farm lending.
          </p>
          <Link
            to="/alert/upload"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-ocean-500 px-3.5 py-2 text-sm font-semibold text-white hover:bg-ocean-600 transition-colors"
          >
            Upload an ALERT file →
          </Link>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* Layer panel */}
        <aside className="col-span-12 lg:col-span-3 bg-white rounded-lg shadow p-4 space-y-5 self-start">
          <div className="flex items-center gap-2 text-sm font-semibold text-navy-900">
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
            Layers
          </div>

          {/* Color layer */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-gray-500">Tract color</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                onClick={() => setColorLayer('INCOME')}
                className={`rounded-md px-3 py-2 text-sm font-medium border ${colorLayer === 'INCOME' ? 'bg-ocean-600 text-white border-ocean-600' : 'bg-white text-gray-700 border-gray-300 hover:border-ocean-400'}`}
              >
                CRA Income
              </button>
              <button
                onClick={() => setColorLayer('MMCT')}
                className={`rounded-md px-3 py-2 text-sm font-medium border ${colorLayer === 'MMCT' ? 'bg-ocean-600 text-white border-ocean-600' : 'bg-white text-gray-700 border-gray-300 hover:border-ocean-400'}`}
              >
                MMCT
              </button>
            </div>
          </div>

          {/* Branch toggle */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-gray-500">Branches</label>
            <label className="mt-2 flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showBranches}
                onChange={(e) => setShowBranches(e.target.checked)}
                className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
              />
              <span className="text-sm text-gray-700">Show branches and LPOs</span>
            </label>
          </div>

          {/* Marketing distribution reach (#3 phase 1b) */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-gray-500">Marketing distribution</label>
            <label className="mt-2 flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showDistribution}
                onChange={(e) => setShowDistribution(e.target.checked)}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">Outline tracts ads reached</span>
            </label>
            {showDistribution && distOverlay?.summary && (
              <div className="mt-2 rounded-md bg-teal-50 border border-teal-100 p-2.5 text-xs text-gray-700 space-y-1">
                {!distOverlay.summary.crosswalkLoaded ? (
                  <p className="text-amber-700">ZIP↔tract crosswalk not loaded yet — only assessment-area-level reach is shown. Run the crosswalk ingest to enable ZIP precision.</p>
                ) : distOverlay.summary.distributionRecords === 0 ? (
                  <p>No distribution recorded for this area yet. Add it on an approved ad's report page.</p>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span>Tracts reached</span>
                      <span className="font-semibold">{distOverlay.summary.reachedTracts}/{distOverlay.summary.totalTracts}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>MMCT reached</span>
                      <span className={`font-semibold ${
                        distOverlay.summary.mmctReachRate != null && distOverlay.summary.nonMmctReachRate != null
                          && distOverlay.summary.mmctReachRate < distOverlay.summary.nonMmctReachRate ? 'text-red-600' : 'text-gray-900'}`}>
                        {distOverlay.summary.mmctReached}/{distOverlay.summary.mmctTracts}
                        {distOverlay.summary.mmctReachRate != null && <> ({distOverlay.summary.mmctReachRate}%)</>}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Non-MMCT reached</span>
                      <span className="font-semibold text-gray-900">
                        {distOverlay.summary.nonMmctReached}/{distOverlay.summary.nonMmctTracts}
                        {distOverlay.summary.nonMmctReachRate != null && <> ({distOverlay.summary.nonMmctReachRate}%)</>}
                      </span>
                    </div>
                    {distOverlay.summary.mmctReachRate != null && distOverlay.summary.nonMmctReachRate != null
                      && distOverlay.summary.mmctReachRate < distOverlay.summary.nonMmctReachRate && (
                      <p className="text-red-600 pt-1 border-t border-teal-100">
                        Advertising reached a smaller share of majority-minority tracts than non-MMCT tracts — review for redlining risk.
                      </p>
                    )}
                    {distOverlay.summary.unmappedZipCount > 0 && (
                      <p className="text-gray-400 pt-1">{distOverlay.summary.unmappedZipCount} ZIP(s) had no tract match.</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Loan dots */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-gray-500">Loan dots</label>
            <div className="mt-2 space-y-2">
              {(['OFF', 'ORIGINATIONS', 'ALL_APPS'] as const).map(opt => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="loanDisplay"
                    value={opt}
                    checked={loanDisplay === opt}
                    onChange={() => setLoanDisplay(opt)}
                    className="border-gray-300 text-ocean-600 focus:ring-ocean-500"
                  />
                  <span className="text-sm text-gray-700">
                    {opt === 'OFF' ? 'Off' : opt === 'ORIGINATIONS' ? 'Originations only' : 'All applications'}
                  </span>
                </label>
              ))}
              {loanDisplay === 'ALL_APPS' && (
                <label className="flex items-center gap-2 cursor-pointer ml-6 mt-1">
                  <input
                    type="checkbox"
                    checked={showPurchased}
                    onChange={(e) => setShowPurchased(e.target.checked)}
                    className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
                  />
                  <span className="text-xs text-gray-600">Include purchased (action 6)</span>
                </label>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="border-t border-gray-100 pt-4">
            <label className="text-xs uppercase tracking-wider font-semibold text-gray-500">Legend</label>
            {colorLayer === 'INCOME' ? (
              <div className="mt-2 space-y-1">
                {(['LOW', 'MODERATE', 'MIDDLE', 'UPPER', 'UNKNOWN'] as const).map(level => (
                  <div key={level} className="flex items-center gap-2 text-xs text-gray-700">
                    <span className="w-4 h-4 rounded-sm border border-white/50" style={{ backgroundColor: INCOME_COLORS[level], opacity: 0.7 }} />
                    {INCOME_LABELS[level]}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <span className="w-4 h-4 rounded-sm" style={{ backgroundColor: MMCT_COLORS.MMCT, opacity: 0.7 }} />
                  Majority-Minority CT
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <span className="w-4 h-4 rounded-sm" style={{ backgroundColor: MMCT_COLORS.NON_MMCT, opacity: 0.7 }} />
                  Non-MMCT
                </div>
              </div>
            )}
            {showBranches && (
              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <span className="w-3 h-3 rounded-full bg-blue-600 ring-2 ring-blue-200" />
                  Main Office
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <span className="w-3 h-3 rounded-full bg-blue-500" />
                  Branch
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <span className="w-3 h-3 rounded-sm bg-amber-500" />
                  LPO / DPO
                </div>
              </div>
            )}
            {loanDisplay !== 'OFF' && (
              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ACTION_COLORS['1'] }} />
                  Originated
                </div>
                {loanDisplay === 'ALL_APPS' && (
                  <>
                    <div className="flex items-center gap-2 text-xs text-gray-700">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ACTION_COLORS['3'] }} />
                      Denied
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-700">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ACTION_COLORS['4'] }} />
                      Withdrawn / Closed
                    </div>
                    {showPurchased && (
                      <div className="flex items-center gap-2 text-xs text-gray-700">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ACTION_COLORS['6'] }} />
                        Purchased
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="border-t border-gray-100 pt-3 text-xs text-red-600 flex items-start gap-1.5">
              <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
              Failed to load map data. Make sure the AA has tracts resolved (refresh demographics on the Assessment Areas page).
            </div>
          )}
        </aside>

        {/* Map */}
        <div
          ref={exportRootRef}
          className="col-span-12 lg:col-span-9 bg-white rounded-lg shadow overflow-hidden relative"
          style={{ height: 'calc(100vh - 240px)', minHeight: 600 }}
        >
          {mapLoading && (
            <div
              data-export-ignore="true"
              className="absolute inset-0 z-10 bg-white/70 flex items-center justify-center"
            >
              <div className="text-sm text-gray-600 flex items-center gap-2">
                <svg className="animate-spin h-5 w-5 text-ocean-600" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading tract boundaries from Census Bureau...
              </div>
            </div>
          )}

          {/* Download PNG — bottom-right; clear of NavigationControl (top-right) and ScaleControl (bottom-left) */}
          <button
            type="button"
            onClick={handleExportPng}
            disabled={exporting || mapLoading || !mapData}
            data-export-ignore="true"
            className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Download map as PNG"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Download PNG'}
          </button>

          <MapEngine
            ref={(r) => { mapRef.current = r; }}
            interactiveLayerIds={['tract-fill']}
            onMapError={(err) => {
              // The Redlining Map failed silently during the Evolve demo on 2026-05-21
              // with controls + data populated but the canvas blank. Likely cause:
              // mid-deploy Vercel asset mismatch caught the mapbox-gl init chunk in
              // the propagation window. Capturing every Mapbox error to Sentry so
              // next time we get diagnostics without relying on the user to F12.
              // See project_redlining_map_intermittent.md memory. MapEngine
              // synthesizes the Error and console.errors; we add the Sentry capture.
              Sentry.captureException(err, {
                tags: {
                  component: 'FairLendingRedliningMap',
                  feature: 'redlining_map_mapbox',
                },
                extra: {
                  mapStyle: 'mapbox://styles/mapbox/light-v11',
                  hasToken: Boolean(MAPBOX_TOKEN),
                  selectedAaId,
                  errorMessage: err?.message,
                },
              });
               
              console.error('[RedliningMap] Mapbox error:', err);
            }}
            onMouseMove={(evt) => {
              const feature = evt.features?.find(f => f.layer?.id === 'tract-fill');
              if (feature) setHoveredTract({ x: evt.point.x, y: evt.point.y, props: feature.properties });
              else setHoveredTract(null);
            }}
            onMouseLeave={() => setHoveredTract(null)}
          >
            {mapData?.tracts && (
              <Source id="tracts" type="geojson" data={mapData.tracts as any}>
                {/* Fill */}
                <Layer
                  id="tract-fill"
                  type="fill"
                  paint={{
                    'fill-color': tractFillExpression,
                    'fill-opacity': 0.55,
                  }}
                />
                {/* Heavier AA outline (every tract in this AA gets the heavier border —
                    Wallace 2026-04-28 spec asked for "heavier border around AA"; with
                    AA-scoped tracts, this approximates a defined AA boundary). */}
                <Layer
                  id="tract-outline"
                  type="line"
                  paint={{
                    'line-color': '#0f172a', // slate-900
                    'line-width': 1.5,
                    'line-opacity': 0.85,
                  }}
                />
                {/* Distribution reach (#3 phase 1b): outline tracts that ads reached. */}
                {showDistribution && reachedTractCodes.length > 0 && (
                  <Layer
                    id="distribution-reach"
                    type="line"
                    filter={['in', ['get', 'tractCode'], ['literal', reachedTractCodes]] as any}
                    paint={{
                      'line-color': '#0d9488', // teal-600
                      'line-width': 3.5,
                      'line-opacity': 0.95,
                    }}
                  />
                )}
              </Source>
            )}

            {/* Loan dots */}
            {visibleLoans.map(l => (
              <Marker
                key={l.id}
                longitude={l.longitude}
                latitude={l.latitude}
                anchor="center"
                onClick={(e) => { e.originalEvent.stopPropagation(); setPopupLoan(l); }}
              >
                <div
                  style={{
                    width: 7, height: 7, borderRadius: '50%',
                    backgroundColor: ACTION_COLORS[l.actionTaken] || '#94a3b8',
                    border: '1px solid white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                  }}
                />
              </Marker>
            ))}

            {/* Branches */}
            {showBranches && mapData?.branches.map(b => {
              const isLpo = b.locationType === 'LPO' || b.locationType === 'DPO';
              const main = b.isMainOffice || b.locationType === 'MAIN_OFFICE';
              const size = main ? 14 : isLpo ? 10 : 11;
              return (
                <Marker
                  key={b.id}
                  longitude={b.longitude}
                  latitude={b.latitude}
                  anchor="center"
                  onClick={(e) => { e.originalEvent.stopPropagation(); setPopupBranch(b); }}
                >
                  <div
                    style={{
                      width: size, height: size,
                      borderRadius: isLpo ? 2 : '50%',
                      backgroundColor: isLpo ? '#f59e0b' : '#2563eb',
                      border: main ? '3px solid #bfdbfe' : '2px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
                      cursor: 'pointer',
                    }}
                    title={b.branchName}
                  />
                </Marker>
              );
            })}

            {/* Tract hover popup */}
            {hoveredTract && (
              <div
                className="pointer-events-none absolute bg-white rounded-md shadow-lg p-2.5 text-xs border border-gray-200"
                style={{ left: hoveredTract.x + 12, top: hoveredTract.y + 12, minWidth: 180, zIndex: 5 }}
              >
                <div className="font-semibold text-navy-900 mb-1">Tract {hoveredTract.props.tractCode || hoveredTract.props.GEOID}</div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                  <span className="text-gray-500">Income:</span>
                  <span style={{ color: INCOME_COLORS[hoveredTract.props.incomeLevel] || '#6b7280' }} className="font-medium">
                    {INCOME_LABELS[hoveredTract.props.incomeLevel] || 'Unknown'}
                  </span>
                  <span className="text-gray-500">MMCT:</span>
                  <span className="font-medium">{hoveredTract.props.isMajorityMinority ? 'Yes' : 'No'}</span>
                  <span className="text-gray-500">Population:</span>
                  <span>{(hoveredTract.props.totalPopulation || 0).toLocaleString()}</span>
                  <span className="text-gray-500">Minority %:</span>
                  <span>{(hoveredTract.props.minorityPct || 0).toFixed(1)}%</span>
                </div>
              </div>
            )}

            {/* Branch click popup */}
            {popupBranch && (
              <Popup
                longitude={popupBranch.longitude}
                latitude={popupBranch.latitude}
                anchor="bottom"
                onClose={() => setPopupBranch(null)}
                closeOnClick={false}
              >
                <div className="text-xs">
                  <div className="font-semibold text-navy-900 flex items-center gap-1">
                    <BuildingOffice2Icon className="h-3 w-3" />
                    {popupBranch.branchName}
                    {popupBranch.isMainOffice && <span className="ml-1 text-blue-700 font-normal">(Main)</span>}
                  </div>
                  <div className="text-gray-600 mt-1">{popupBranch.locationType}</div>
                  <div className="text-gray-600">{popupBranch.address}</div>
                  <div className="text-gray-600">{popupBranch.city}, {popupBranch.state} {popupBranch.zip}</div>
                </div>
              </Popup>
            )}

            {/* Loan click popup */}
            {popupLoan && (
              <Popup
                longitude={popupLoan.longitude}
                latitude={popupLoan.latitude}
                anchor="bottom"
                onClose={() => setPopupLoan(null)}
                closeOnClick={false}
              >
                <div className="text-xs">
                  <div className="font-semibold text-navy-900">{popupLoan.actionTakenLabel}</div>
                  <div className="text-gray-600 mt-1">Tract {popupLoan.tractCode}</div>
                  {popupLoan.loanAmount != null && (
                    <div className="text-gray-600">Amount: ${popupLoan.loanAmount.toLocaleString()}</div>
                  )}
                  {popupLoan.loanPurpose && (
                    <div className="text-gray-600">Purpose code: {popupLoan.loanPurpose}</div>
                  )}
                </div>
              </Popup>
            )}
          </MapEngine>
        </div>
      </div>
    </div>
  );
}
